import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
} from "@mui/material";
import signup from "../../assets/images/background.png";
import { useApi } from "../../api/useApi";
import { useParams, useNavigate } from "react-router-dom";

// === Helper for color ===
const getColorForScore = (score) => {
  if (score < 40) return "#ff4d4d"; // red
  if (score < 70) return "#ffcc00"; // yellow
  return "#4caf50"; // green
};

// === Reusable Speedometer Card ===
const SpeedometerCard = ({ title, subtitle, score, domain }) => {
    const size = 160;
    const strokeWidth = 14;
    const radius = (size - strokeWidth) / 2;
    const center = size / 2;
    const circumference = Math.PI * radius;

    const normalizedScore = Math.min(Math.max(score, 0), 100);
    const arcLength = (normalizedScore / 100) * circumference;

    const navigate = useNavigate();

    // Compute pointer angle (180° range)
    const angle = (normalizedScore / 100) * Math.PI;
    const pointerX = center - radius * Math.cos(angle);
    const pointerY = center - radius * Math.sin(angle);

    // Function to determine gradient color based on score
    const getColorForScore = (s) => {
        if (s < 25) return "red";
        if (s < 50) return "yellow";
        if (s < 75) return "#00aaff";
        return "green";
    };

    const pointerColor = getColorForScore(score);

    const handlePreviewClick = () => {
        navigate("/recommendation-page", {
            state: {
                category: title,
                score,
                domain,
            },
        });
    };

    return (
        <Box
      sx={{
        backgroundColor: "rgba(255,255,255,0.95)",
        borderRadius: "14px",
        boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
        p: 2.5,
        display: "flex",
        width: "430px",
        alignItems: "center",
        gap: 2,
        transition: "all 0.25s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
        },
      }}
    >
      {/* Gauge */}
      <Box
        sx={{
          width: 160,
          height: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width={size} height={size / 2}>
          {/* Background track */}
          <path
            d={`M ${center - radius},${center}
               A ${radius},${radius} 0 0 1 ${center + radius},${center}`}
            stroke="#eee"
            strokeWidth={strokeWidth}
            fill="none"
          />

          {/* Gradient arc */}
          <defs>
            <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="red" />
              <stop offset="30%" stopColor="yellow" />
              <stop offset="70%" stopColor="#00aaff" />
              <stop offset="100%" stopColor="green" />
            </linearGradient>
          </defs>

          <path
            d={`M ${center - radius},${center}
               A ${radius},${radius} 0 0 1 ${center + radius},${center}`}
            stroke="url(#gaugeGradient)"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${arcLength} ${circumference}`}
            strokeLinecap="round"
          />

          {/* Circle pointer */}
          <circle
            cx={pointerX}
            cy={pointerY}
            r={6}
            fill={pointerColor}
            stroke="white"
            strokeWidth="2"
          />

          {/* Score Text */}
          <text
            x="50%"
            y="75%"
            textAnchor="middle"
            fontSize="22"
            fontWeight="bold"
            fontFamily="Akkurat"
            fill={pointerColor}
          >
            {Math.round(score)}%
          </text>
        </svg>
      </Box>

      {/* Text Section */}
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontFamily: "Akkurat-bold",
            color: "#333",
            mb: 0.5,
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: "#555",
              mb: 1,
              fontFamily: "Akkurat",
            }}
          >
            {subtitle}
          </Typography>
        )}
        {
          !window.location.href.includes('admin') && (
          <Typography
            variant="body2"
            sx={{
              color: "#1976d2",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 500,
              fontFamily: "Akkurat",
            }}
            onClick={handlePreviewClick}
          >
            Preview Detailed Recommendations
          </Typography>
          )
        }

      </Box>
    </Box>
    );
};

// === Main Page ===
const MaturityAssessment = () => {
  const [categoryScores, setCategoryScores] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { request } = useApi();
  const { domain, email } = useParams();

  // === Fetch Data ===
  useEffect(() => {
    const fetchScores = async () => {
      setLoading(true);
      try {
        const response = await request({
          url: `/evaluations/users/${email}/domain/${domain}/category-raw-scores`,
        });
        setCategoryScores(response.categoryScores || {});
      } catch (err) {
        console.error(err);
        setError("Error fetching scores");
      } finally {
        setLoading(false);
      }
    };

    fetchScores();
  }, [domain, email, request]);

  const handleDownloadReport = async () => {
    try {
      const response = await request({
        url: `/evaluations/export-report/${domain}`,
        method: "GET",
        responseType: "blob",
      });
      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${domain}_Maturity_Report.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      setError("Failed to download report");
    }
  };

  // === Calculate Average Score ===
  const averageScore =
    Object.values(categoryScores).length > 0
      ? Object.values(categoryScores).reduce((sum, c) => sum + c.score, 0) /
        Object.values(categoryScores).length
      : 0;

  return (
    <Box
      sx={{
        minHeight: "90vh",
        width: "83vw",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Background Layers */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url(${signup})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.2,
          zIndex: -1,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "conic-gradient(from -7.72deg at 100% 100%, #000000 0deg, #49454F 294.45deg, #000000 360deg)",
          opacity: 1,
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          zIndex: -2,
        }}
      />

      {/* Main Content */}
      <Box
  sx={{
    m: 2,
    width: "92vw",
    height: "85%",
    borderRadius: "20px",
    padding: 4,
    overflowY: "auto",
    position: "relative", // make children absolute relative to this box
    background: "rgba(255, 255, 255, 0.12)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
    backdropFilter: "blur(18px) saturate(130%) brightness(0.8)",
    WebkitBackdropFilter: "blur(18px) saturate(130%) brightness(0.8)",
    color: "#f1f1f1",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    "&::-webkit-scrollbar": { width: "8px", opacity: 0 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(255,255,255,0.2)",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(255,255,255,0.5)",
    },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    "&:hover::-webkit-scrollbar": { opacity: 1 },
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(255,255,255,0.3) transparent",
  }}
>
  {/* Go Back Button (sticky top-right) */}
  <Button
    variant="contained"
    sx={{
      position: "absolute",
      top: 16,
      right: 16,
      zIndex: 10,
      backgroundColor: "#1976d2",
      color: "#fff",
      "&:hover": { backgroundColor: "#115293" },
    }}
    onClick={() => window.history.back()}
  >
    Go Back
  </Button>

  {/* Title */}
  <Typography
    variant="h4"
    sx={{
      fontWeight: 600,
      color: "#fff",
      textAlign: "center",
      mb: 2,
      letterSpacing: "0.5px",
    }}
  >
    {domain} Maturity Assessment
  </Typography>

  {/* Loader */}
  {loading && (
    <Box display="flex" justifyContent="center" alignItems="center" mt={5}>
      <CircularProgress sx={{ color: "#fff" }} />
    </Box>
  )}

  {/* Error */}
  {error && (
    <Typography color="error" textAlign="center" mt={2}>
      {error}
    </Typography>
  )}

  {/* Speedometer Cards */}
  {!loading && !error && Object.keys(categoryScores).length > 0 && (
    <Box
      sx={{
        mt: 4,
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: 4,
        p: 3,
        overflowY: "auto",
      }}
    >
      {Object.entries(categoryScores).map(([category, details]) => (
        <SpeedometerCard
          key={category}
          title={category}
          subtitle={`${domain} Category`}
          score={details.score}
          level={details.level}
        />
      ))}
    </Box>
  )}
</Box>

    </Box>
  );
};

export default MaturityAssessment;